import { ObjectFieldTemplateProps } from "@rjsf/utils";
import { useFormSchema } from "src/api/hooks/useFormSchema";
import { useState } from "react";

/**
 * Custom ObjectFieldTemplate to control how object fields (containers) are rendered
 * This controls the wrapper/container for nested object fields
 */
export const CustomObjectFieldTemplate = (props: ObjectFieldTemplateProps) => {
  const { data: schema } = useFormSchema();
  const {
    title,
    description,
    properties,
    required,
    disabled,
    readonly,
    uiSchema,
    onAddProperty,
  } = props;

  const [showDescription, setShowDescription] = useState(false);
  const errorColor = schema?.theme?.colors?.error;
  const textColor = schema?.theme?.colors?.text;
  const primaryColor = schema?.theme?.colors?.primary;

  return (
    <div
      className={`object-container container ${title}-container w-full max-w-full box-border`}
    >
      {/* Title */}
      {title && (
        <h3 className={`object-title`}>
          <div className="flex items-center justify-center gap-2">
            <span>{title}</span>
            {required && (
              <span
                className={`ml-1 ${!errorColor ? "text-PrimaryRed" : ""}`}
                style={{
                  ...(errorColor && { color: errorColor }),
                }}
              >
                *
              </span>
            )}
            {description && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowDescription(!showDescription);
                }}
                className="description-toggle-btn"
                aria-label={
                  showDescription ? "Hide description" : "Show description"
                }
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "4px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.2s ease",
                  borderRadius: "50%",
                  color: primaryColor || "#4CAF50",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.05)";
                  e.currentTarget.style.transform = "scale(1.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.transform = "scale(1)";
                }}
              >
                <i
                  className={`fa-solid ${
                    showDescription ? "fa-circle-info" : "fa-circle-info"
                  }`}
                  style={{
                    fontSize: "20px",
                    opacity: showDescription ? 1 : 0.6,
                  }}
                />
              </button>
            )}
          </div>
          <div className="fading-border"></div>
        </h3>
      )}

      {/* Description - toggleable */}
      {description && showDescription && (
        <div
          className="description-content"
          style={{
            fontSize: "14px",
            fontStyle: "italic",
            marginBottom: "8px",
            marginTop: "8px",
            padding: "8px 12px",
            backgroundColor: "rgba(0, 0, 0, 0.02)",
            borderRadius: "6px",
            borderLeft: `3px solid ${primaryColor || "#4CAF50"}`,
            color: textColor || "#666",
            animation: "fadeIn 0.3s ease-in",
          }}
        >
          {description}
        </div>
      )}

      {/* Properties (nested fields) */}
      <div className="flex flex-col gap-2 mt-4">
        {properties.map((prop) => prop.content)}
      </div>
    </div>
  );
};
